import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  MapPin, Phone, Mail, MessageCircle, Facebook, Instagram, Linkedin,
  Youtube, Shield, Award, Star, Home, Send, ChevronRight,
  Loader2, Building2, BedDouble, Bath, Car, Maximize, Users, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PublicMicrositePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const chatEndRef = useRef(null);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    interest_type: 'compra'
  });

  useEffect(() => {
    loadMicrosite();
  }, [slug]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadMicrosite = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/microsites/public/${slug}`);
      setData(res.data);
    } catch (err) {
      setError('Asesor no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/microsites/public/${slug}/contact`, contactForm);
      alert('Mensaje enviado. El asesor se pondrá en contacto contigo pronto.');
      setShowContactForm(false);
      setContactForm({ name: '', email: '', phone: '', message: '', interest_type: 'compra' });
    } catch (err) {
      alert('Error al enviar mensaje');
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || sendingMessage) return;
    
    const userMessage = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatInput('');
    setSendingMessage(true);
    
    try {
      const res = await axios.post(`${API_URL}/api/customer-bot/chat/${slug}`, {
        message: userMessage,
        session_id: sessionId
      });
      
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      if (!sessionId) {
        setSessionId(res.data.session_id);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Lo siento, hubo un problema. Por favor intenta de nuevo o contacta directamente al asesor.' 
      }]);
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-20 h-20 mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold text-gray-700">{error}</h1>
          <p className="text-gray-500 mt-2">El micrositio que buscas no existe</p>
        </div>
      </div>
    );
  }

  const { microsite, properties, alliances, ally_properties } = data;
  const themeColor = microsite.theme_color || '#4F46E5';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div 
        className="relative h-[400px] md:h-[500px]"
        style={{ 
          background: microsite.cover_photo_url 
            ? `linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%), url(${microsite.cover_photo_url}) center/cover`
            : `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}99 100%)`
        }}
      >
        {/* UPIM Badge */}
        <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm">
          <Shield className="w-5 h-5 text-indigo-600" />
          <span className="font-semibold text-gray-800 text-sm">Asesor UPIM Certificado</span>
          {microsite.is_verified && (
            <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">Verificado</span>
          )}
        </div>

        {/* Profile Card */}
        <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-full max-w-md px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center">
            {microsite.profile_photo_url ? (
              <img 
                src={microsite.profile_photo_url} 
                alt={microsite.display_name}
                className="w-28 h-28 rounded-full mx-auto -mt-20 border-4 border-white shadow-lg object-cover"
              />
            ) : (
              <div 
                className="w-28 h-28 rounded-full mx-auto -mt-20 border-4 border-white shadow-lg flex items-center justify-center text-white text-4xl font-bold"
                style={{ backgroundColor: themeColor }}
              >
                {microsite.display_name?.charAt(0) || 'A'}
              </div>
            )}
            
            <h1 className="text-2xl font-bold text-gray-900 mt-4">{microsite.display_name}</h1>
            {microsite.business_name && (
              <p className="text-gray-500">{microsite.business_name}</p>
            )}
            {microsite.tagline && (
              <p className="text-gray-600 mt-2 italic">"{microsite.tagline}"</p>
            )}
            
            <div className="flex items-center justify-center gap-2 mt-3 text-gray-500 text-sm">
              <MapPin className="w-4 h-4" />
              <span>{microsite.city}, {microsite.state}</span>
            </div>
            
            {/* Specialties */}
            {microsite.specialties?.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {microsite.specialties.map((spec, i) => (
                  <span 
                    key={i}
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: `${themeColor}20`, color: themeColor }}
                  >
                    {spec}
                  </span>
                ))}
              </div>
            )}

            {/* Contact Buttons */}
            <div className="flex gap-2 mt-6">
              {microsite.public_whatsapp && (
                <a
                  href={`https://wa.me/${microsite.public_whatsapp.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp
                </a>
              )}
              <button
                onClick={() => setShowContactForm(true)}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-white transition-colors"
                style={{ backgroundColor: themeColor }}
              >
                <Mail className="w-5 h-5" />
                Contactar
              </button>
            </div>

            {/* Social Links */}
            <div className="flex justify-center gap-3 mt-4">
              {microsite.facebook_url && (
                <a href={microsite.facebook_url} target="_blank" rel="noopener noreferrer" 
                   className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                  <Facebook className="w-5 h-5 text-gray-600" />
                </a>
              )}
              {microsite.instagram_url && (
                <a href={microsite.instagram_url} target="_blank" rel="noopener noreferrer"
                   className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                  <Instagram className="w-5 h-5 text-gray-600" />
                </a>
              )}
              {microsite.linkedin_url && (
                <a href={microsite.linkedin_url} target="_blank" rel="noopener noreferrer"
                   className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                  <Linkedin className="w-5 h-5 text-gray-600" />
                </a>
              )}
              {microsite.youtube_url && (
                <a href={microsite.youtube_url} target="_blank" rel="noopener noreferrer"
                   className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                  <Youtube className="w-5 h-5 text-gray-600" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Bio */}
          {microsite.bio && (
            <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Sobre mí</h2>
              <p className="text-gray-600 leading-relaxed">{microsite.bio}</p>
            </div>
          )}

          {/* Properties */}
          {properties?.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Home className="w-6 h-6" style={{ color: themeColor }} />
                Mis Propiedades ({properties.length})
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map(prop => (
                  <PropertyCard key={prop.id} property={prop} themeColor={themeColor} />
                ))}
              </div>
            </div>
          )}

          {/* Alliances */}
          {alliances?.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Users className="w-6 h-6" style={{ color: themeColor }} />
                Aliados Comerciales ({alliances.length})
              </h2>
              <div className="flex flex-wrap gap-4">
                {alliances.map(ally => (
                  <div key={ally.id} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: themeColor }}
                    >
                      {ally.name?.charAt(0) || 'A'}
                    </div>
                    <span className="font-medium text-gray-800">{ally.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ally Properties */}
          {ally_properties?.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Home className="w-6 h-6" style={{ color: themeColor }} />
                Propiedades de Aliados
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {ally_properties.map(prop => (
                  <PropertyCard key={prop.id} property={prop} themeColor={themeColor} isAlly />
                ))}
              </div>
            </div>
          )}

          {/* Testimonials */}
          {microsite.testimonials?.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Star className="w-6 h-6" style={{ color: themeColor }} />
                Testimonios
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {microsite.testimonials.map((test, i) => (
                  <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex gap-1 mb-3">
                      {[...Array(test.rating || 5)].map((_, j) => (
                        <Star key={j} className="w-5 h-5 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-gray-600 italic mb-4">"{test.text}"</p>
                    <p className="font-medium text-gray-900">— {test.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* UPIM Badge */}
          <div 
            className="rounded-2xl p-8 text-white text-center"
            style={{ background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}cc 100%)` }}
          >
            <Shield className="w-16 h-16 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Asesor Certificado UPIM</h3>
            <p className="opacity-90 max-w-xl mx-auto">
              Miembro de la Unión de Profesionales Inmobiliarios de México. 
              Respaldado por estándares de profesionalismo y ética.
            </p>
            {microsite.upim_member_since && (
              <p className="mt-4 opacity-75 text-sm">
                Miembro desde {new Date(microsite.upim_member_since).getFullYear()}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Chat Widget */}
      <button
        onClick={() => setShowChat(!showChat)}
        className="fixed bottom-6 right-6 w-16 h-16 rounded-full shadow-lg flex items-center justify-center text-white z-40"
        style={{ backgroundColor: themeColor }}
      >
        {showChat ? <X className="w-7 h-7" /> : <MessageCircle className="w-7 h-7" />}
      </button>

      {/* Chat Panel */}
      {showChat && (
        <div className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl overflow-hidden z-40">
          <div 
            className="p-4 text-white"
            style={{ backgroundColor: themeColor }}
          >
            <h3 className="font-semibold">Chat con {microsite.display_name}</h3>
            <p className="text-sm opacity-90">Respuesta automática con IA</p>
          </div>
          
          <div className="h-80 overflow-y-auto p-4 space-y-3">
            {chatMessages.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                <MessageCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">¡Hola! ¿En qué puedo ayudarte?</p>
              </div>
            )}
            {chatMessages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                  style={msg.role === 'user' ? { backgroundColor: themeColor } : {}}
                >
                  <p className="text-sm">{msg.content}</p>
                </div>
              </div>
            ))}
            {sendingMessage && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl p-3">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Escribe tu mensaje..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': themeColor }}
              />
              <button
                onClick={sendChatMessage}
                disabled={sendingMessage}
                className="p-3 rounded-full text-white"
                style={{ backgroundColor: themeColor }}
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Contactar a {microsite.display_name}</h2>
            </div>
            <form onSubmit={submitContact} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  required
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Interés</label>
                <select
                  value={contactForm.interest_type}
                  onChange={(e) => setContactForm({ ...contactForm, interest_type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="compra">Comprar</option>
                  <option value="renta">Rentar</option>
                  <option value="venta">Vender mi propiedad</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mensaje</label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Cuéntanos qué estás buscando..."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="flex-1 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 text-white rounded-lg"
                  style={{ backgroundColor: themeColor }}
                >
                  Enviar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Property Card Component
function PropertyCard({ property, themeColor, isAlly = false }) {
  const opNames = {
    venta: 'Venta',
    renta: 'Renta',
    venta_renta: 'Venta/Renta'
  };
  
  const typeNames = {
    casa: 'Casa',
    departamento: 'Depto',
    terreno: 'Terreno',
    local_comercial: 'Local',
    oficina: 'Oficina',
    bodega: 'Bodega'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <div className="relative h-48">
        {property.images?.[0] ? (
          <img 
            src={typeof property.images[0] === 'string' ? property.images[0] : property.images[0].url}
            alt={property.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <Home className="w-12 h-12 text-gray-400" />
          </div>
        )}
        
        <div 
          className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-medium"
          style={{ backgroundColor: themeColor }}
        >
          {opNames[property.operation_type] || 'Venta'}
        </div>
        
        {isAlly && (
          <div className="absolute top-3 right-3 px-2 py-1 bg-amber-500 text-white rounded-full text-xs">
            Aliado
          </div>
        )}
        
        {property.is_featured && (
          <div className="absolute top-3 right-3 px-2 py-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-full text-xs flex items-center gap-1">
            <Star className="w-3 h-3" />
            Destacada
          </div>
        )}
      </div>
      
      <div className="p-4">
        <p className="text-xs text-gray-500 mb-1">
          {typeNames[property.property_type] || property.property_type} en {property.city}
        </p>
        <h3 className="font-semibold text-gray-900 line-clamp-1">{property.title}</h3>
        
        <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
          {property.bedrooms > 0 && (
            <span className="flex items-center gap-1">
              <BedDouble className="w-4 h-4" />
              {property.bedrooms}
            </span>
          )}
          {property.bathrooms > 0 && (
            <span className="flex items-center gap-1">
              <Bath className="w-4 h-4" />
              {property.bathrooms}
            </span>
          )}
          {property.parking_spaces > 0 && (
            <span className="flex items-center gap-1">
              <Car className="w-4 h-4" />
              {property.parking_spaces}
            </span>
          )}
          {property.construction_area > 0 && (
            <span className="flex items-center gap-1">
              <Maximize className="w-4 h-4" />
              {property.construction_area}m²
            </span>
          )}
        </div>
        
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <span className="text-lg font-bold" style={{ color: themeColor }}>
            ${(property.price || 0).toLocaleString('es-MX')}
          </span>
          <a
            href={`${API_URL}/api/fichas/property/${property.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium flex items-center gap-1 hover:underline"
            style={{ color: themeColor }}
          >
            Ver ficha <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
